import type { MediaItem } from '@/types/posts';
import { PLATFORM_MEDIA_LIMITS } from '@/constants/platformMediaLimits';

/** The still image a preview can draw for a media item (a video's poster frame). */
export const visualUrl = (item: MediaItem): string =>
  item.type === 'video' ? item.thumbnail || item.url : item.url;

/** MM:SS label for a duration in seconds; null when unknown. */
export const durationLabel = (seconds?: number): string | null => {
  if (!seconds) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/** TikTok Photo Mode carousel limit (matches the publisher's photo requirements). */
export const TIKTOK_PHOTO_LIMIT = PLATFORM_MEDIA_LIMITS.tiktokPhotoImages;

export interface GbpMediaSelection {
  kind: 'image' | 'video-thumbnail' | 'none';
  url: string | null;
  /** True when the post has a video that Google cannot show (drives the in-card notice). */
  hasVideo: boolean;
}

/**
 * The single photo a Google Business post publishes. Mirrors the backend exactly
 * (backend/src/services/gmb-publisher.service.ts ~l.1010-1033): the FIRST image if
 * there is one; else the first VIDEO's thumbnail as the photo; else text-only.
 * Google posts cannot contain video.
 */
export const selectGbpMedia = (media: MediaItem[]): GbpMediaSelection => {
  const image = media.find((m) => m.type === 'image');
  const video = media.find((m) => m.type === 'video');
  const hasVideo = video !== undefined;
  if (image) return { kind: 'image', url: image.url, hasVideo };
  if (video?.thumbnail) return { kind: 'video-thumbnail', url: video.thumbnail, hasVideo };
  return { kind: 'none', url: null, hasVideo };
};

export interface PinterestMediaSelection {
  kind: 'image-pin' | 'video-pin' | 'none';
  /** Image pin: the first image. Video pin: its cover (first image, else the video's thumbnail); null if neither exists. */
  url: string | null;
}

/**
 * What a Pinterest pin publishes. Mirrors the backend exactly
 * (backend/src/workers/scheduleWorker.ts ~l.2975-3012, publishToPinterest):
 * a post with any video publishes the first VIDEO as a video pin, with the first
 * IMAGE as its cover, else the video's thumbnail; otherwise the first IMAGE as an
 * image pin. Only one item is published either way.
 */
export const selectPinterestMedia = (media: MediaItem[]): PinterestMediaSelection => {
  const image = media.find((m) => m.type === 'image');
  const video = media.find((m) => m.type === 'video');
  if (video) return { kind: 'video-pin', url: image?.url ?? video.thumbnail ?? null };
  if (image) return { kind: 'image-pin', url: image.url };
  return { kind: 'none', url: null };
};
